import React, { useMemo, useState } from 'react';
import Logo from '../../components/Logo';
import PublicNav from '../../components/PublicNav';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Seo } from '../../components/Seo';
import guidesData from '../../content/guides.generated.json';

const BASE_URL = 'https://verifaeye.com.au';

function categoryPillColor(cat) {
  switch (cat) {
    case 'Claiming': return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'Documenting': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'Recovery': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    default: return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

export default function GuidesIndex() {
  const { isAuthenticated } = useAuth();
  const guides = guidesData.guides || [];
  const categories = useMemo(() => {
    const set = new Set(guides.map((g) => g.category).filter(Boolean));
    return Array.from(set).sort();
  }, [guides]);
  const [active, setActive] = useState('All');
  const visible = active === 'All' ? guides : guides.filter((g) => g.category === active);

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: `${BASE_URL}/guides` },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="guides-index-page">
      <Seo
        title="Guides from the Assessor's Side of the Desk | VerifAEye"
        description="Field-tested guides on documenting household contents, preparing insurance evidence and getting settlement-ready — written from the assessor's side of the desk."
        path="/guides"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <PublicNav isAuthenticated={isAuthenticated} current="/guides" />

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <p className="text-blue-600 font-medium mb-3 tracking-wide text-sm">FIELD NOTES</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Guides from the Assessor&apos;s Side of the Desk
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
            Short, practical guides on documenting household contents, capturing the right evidence up front, and making claims that assessors can approve without argument.
          </p>

          {categories.length > 1 && (
            <div className="mt-10 flex flex-wrap gap-2" data-testid="guides-filter-row">
              {['All', ...categories].map((c) => {
                const isActive = active === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setActive(c)}
                    data-testid={`guides-filter-${c.toLowerCase()}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition ${
                      isActive
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {c}
                    {c !== 'All' && (
                      <span className={`ml-2 text-xs ${isActive ? 'opacity-80' : 'text-slate-400'}`}>
                        {guides.filter((g) => g.category === c).length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-16 grid gap-6 sm:grid-cols-2">
            {guides.length === 0 && (
              <p className="text-slate-500 col-span-full" data-testid="guides-empty">
                No guides published yet — check back soon.
              </p>
            )}
            {visible.map((g) => (
              <Link
                key={g.slug}
                to={`/guides/${g.slug}`}
                data-testid={`guide-card-${g.slug}`}
                className="group block rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
              >
                <div className="flex items-center gap-2 text-xs">
                  {g.category && (
                    <span
                      data-testid={`guide-card-category-${g.slug}`}
                      className={`inline-block px-2 py-0.5 rounded-full border font-medium ${categoryPillColor(g.category)}`}
                    >
                      {g.category}
                    </span>
                  )}
                  <span className="uppercase tracking-wider text-slate-400">{g.readTime}</span>
                </div>
                <h2 className="mt-3 text-xl font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                  {g.title}
                </h2>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">{g.description}</p>
                <p className="mt-4 text-blue-600 text-sm font-medium">Read guide →</p>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="text-slate-600 text-sm ml-2">— The Proof is in the Detail</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/guides" className="text-slate-500 hover:text-slate-700">Guides</Link>
            <Link to="/terms" className="text-slate-500 hover:text-slate-700">Terms of Service</Link>
            <Link to="/privacy" className="text-slate-500 hover:text-slate-700">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
